import BaseSetup from '@/components/BaseSetup';
import useUserFavoriteService from '@/services/user/UserFavoriteService';
import { FAVORITE_TYPE } from '@/typings/enums/FAVORITE_TYPE';
import { Reactive } from '@/utils/decorators';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { onClickOutside } from '@vueuse/core';
import { debounce, throttle } from 'lodash-es';
import { nextTick, ref } from 'vue';

export type SharedMainNavEmits = {};
export type SharedMainNavProps = {
	unreadNewsCount?: number
};

export default class SharedMainNavSetup extends BaseSetup {

	static readonly defaultProps = { unreadNewsCount: 0 };

	readonly _navMain = ref<OrionNavMain[]>();

	private get mobileNavChildrenCloneWrapper () { return document.getElementById(this.wrapperId) }
	private get userTypeNavigation () {
		if (this.self.isAgency()) {
			return this.agencyNavigation;
		}
		else if (this.self.isClient()) {
			return this.clientNavigation;
		}
		else if (this.self.isTempWorker()) {
			return this.tempWorkerNavigation;
		}
		return [];
	}

	private get agencyNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: this.$t('common.clients'),
				fontIcon: 'icon-shopping-building',
				if: this.self.canOneOf(
					'ClientUserRead',
					'ClientContractRead',
					'BillRead',
					'RegisterRead',
					'HandicappedWorkerListRead',
					'ClientProvisionDocumentRead',
					'TimeTicketRead',
				),
				children: [
					{
						label: this.$t('user.clientUsersList'),
						favoritetype: FAVORITE_TYPE.MesClientsClients,
						to: {
							name: 'User',
							// params: { type: 'clients' },
						},
						if: this.self.can('ClientUserRead'),
					},
					{
						label: this.$t('common.navigation.documents'),
						line: true,
						if: this.self.canOneOf(
							'ClientContractRead',
							'BillRead',
							'RegisterRead',
							'HandicappedWorkerListRead',
							'ClientProvisionDocumentRead',
						),
					},
					{
						label: this.$t('common.navigation.contracts'),
						favoritetype: FAVORITE_TYPE.MesClientsContrats,
						to: { name: 'ClientContract' },
						if: this.self.can('ClientContractRead'),
					},
					{
						label: this.$t('common.navigation.invoices'),
						favoritetype: FAVORITE_TYPE.MesClientsFactures,
						to: { name: 'Bill' },
						if: this.self.can('BillRead'),
					},
					{
						label: this.$t('common.personnelRegisters'),
						favoritetype: FAVORITE_TYPE.MesClientsRegistresDuPersonnel,
						to: { name: 'Register' },
						if: this.self.can('RegisterRead'),
					},
					{
						label: this.$t('common.navigation.handicappedWorkersList'),
						favoritetype: FAVORITE_TYPE.MesClientsListeTravailleursHandicapes,
						to: { name: 'HandicappedWorkerList' },
						if: this.self.can('HandicappedWorkerListRead'),
					},
					{
						label: this.$t('business.statistics.documents.provisionDocuments'),
						favoritetype: FAVORITE_TYPE.MesClientsDocumentsMisADisposition,
						to: { name: 'ClientProvisionDocument' },
						if: this.self.can('ClientProvisionDocumentRead'),
					},
					{
						label: this.$t('common.processings.label'),
						line: true,
						if: this.self.canOneOf('TimeTicketRead', 'ClientContractRead'),
					},
					{
						label: this.$t('common.timeTickets'),
						favoritetype: FAVORITE_TYPE.MesClientsRelevesHeures,
						to: {
							name: 'TimeTicketYear',
							params: { year: new Date().getFullYear().toString() },
						},
						if: this.self.can('TimeTicketRead'),
					},
					{
						label: this.$t('documents.contractExtensions'),
						favoritetype: FAVORITE_TYPE.MesClientsProlongationsContrats,
						to: { name: 'ContractProlongation' },
						if: this.self.can('ClientContractRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.navigation.tempWorkers'),
				fontIcon: 'icon-interface-user-multiple',
				if: this.self.canOneOf(
					'TempWorkerUserRead',
					'TempWorkerContractRead',
					'SalarySlipRead',
					'CertificateRead',
					'WithholdingTaxAttestationRead',
					'TripAttestationRead',
					'PoleEmploiAttestationRead',
					'TempWorkerProvisionDocumentRead',
				),
				children: [
					{
						label: this.$t('common.navigation.tempWorkersList'),
						favoritetype: FAVORITE_TYPE.MesInterimairesInterimaires,
						to: { name: 'TempWorker' },
						if: this.self.can('TempWorkerUserRead'),
					},
					{
						label: this.$t('common.navigation.documents'),
						line: true,
						if: this.self.canOneOf(
							'TempWorkerContractRead',
							'SalarySlipRead',
							'CertificateRead',
							'WithholdingTaxAttestationRead',
							'TripAttestationRead',
							'PoleEmploiAttestationRead',
							'TempWorkerProvisionDocumentRead',
						),
					},
					{
						label: this.$t('common.navigation.contracts'),
						favoritetype: FAVORITE_TYPE.MesInterimairesContrats,
						to: { name: 'TempWorkerContract' },
						if: this.self.can('TempWorkerContractRead'),
					},
					{
						label: this.$t('common.paySlips'),
						favoritetype: FAVORITE_TYPE.MesInterimairesBulletinsSalaire,
						to: { name: 'SalarySlip' },
						if: this.self.can('SalarySlipRead'),
					},
					{
						label: this.$t('common.workCertificates'),
						favoritetype: FAVORITE_TYPE.MesInterimairesCertificatsTravail,
						to: { name: 'Certificate' },
						if: this.self.can('CertificateRead'),
					},
					{
						label: this.$t('common.taxCertificates'),
						favoritetype: FAVORITE_TYPE.MesInterimairesAttestationsImpots,
						to: { name: 'WithholdingTaxAttestation' },
						if: this.self.can('WithholdingTaxAttestationRead'),
					},
					{
						label: this.$t('business.contractProlongation.justificatory'),
						favoritetype: FAVORITE_TYPE.MesInterimairesJustificatifsDeplacements,
						to: { name: 'TripAttestation' },
						if: this.self.can('TripAttestationRead'),
					},
					{
						label: this.$t('common.navigation.employerAttestationsFranceTravail'),
						favoritetype: FAVORITE_TYPE.MesInterimairesAttestationsPoleEmploi,
						to: { name: 'PoleEmploiAttestation' },
						if: this.self.can('PoleEmploiAttestationRead'),
					},
					{
						label: this.$t('business.statistics.documents.provisionDocuments'),
						favoritetype: FAVORITE_TYPE.MesInterimairesDocumentsMisADisposition,
						to: { name: 'TempWorkerProvisionDocument' },
						if: this.self.can('TempWorkerProvisionDocumentRead'),
					},
					{
						label: this.$t('common.processings.label'),
						line: true,
						if: this.self.can('AdvancedPaymentDemandRead'),
					},
					{
						label: this.$t('common.navigation.advancePaymentRequests'),
						favoritetype: FAVORITE_TYPE.MesInterimairesDemandesAcompte,
						to: { name: 'AdvancedPaymentDemand' },
						if: this.self.can('AdvancedPaymentDemandRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.navigation.orders'),
				fontIcon: 'icon-shopping-cart-1',
				if: this.self.canOneOf('OrderRead', 'OrderToolbox') && this.self.can('TempWorkerUserRead'),
				children: [
					{
						label: this.$t('common.navigation.overview'),
						favoritetype: FAVORITE_TYPE.CommandesVueDensemble,
						to: { name: 'OrderDashboard' },
						if: this.self.can('OrderRead') && this.self.can('TempWorkerUserRead'),
					},
					{
						label: this.$t('common.navigation.orders'),
						favoritetype: FAVORITE_TYPE.CommandesCommandes,
						to: { name: 'Order' },
						if: this.self.can('OrderRead') && this.self.can('TempWorkerUserRead'),
					},
					{
						label: this.$t('common.toolbox'),
						favoritetype: FAVORITE_TYPE.CommandesBoiteAOutils,
						to: { name: 'ToolboxTalentPool' },
						if: this.self.can('OrderToolbox') && this.self.can('TempWorkerUserRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.navigation.organization'),
				fontIcon: 'icon-interface-hierarchy-2',
				if: this.self.canOneOf(
					'OrganizationRead',
					'UserRead',
					'OrganizationRoleRead',
					'TagRead',
				),
				children: [
					{
						label: this.$t('common.navigation.visionOverview'),
						favoritetype: FAVORITE_TYPE.OrganisationVisionDensemble,
						to: { name: 'OrganizationDashboard' },
						if: this.self.can('OrganizationRead'),
					},
					{
						label: this.$t('common.companies'),
						favoritetype: FAVORITE_TYPE.OrganisationSocietes,
						to: { name: 'OrganizationCompany' },
						if: this.self.can('OrganizationRead'),
					},
					{
						label: this.$t('common.navigation.users'),
						favoritetype: FAVORITE_TYPE.OrganisationMesUtilisateurs,
						to: { name: 'OrganizationUser' },
						if: this.self.can('UserRead'),
					},
					{
						label: this.$t('common.navigation.roles'),
						favoritetype: FAVORITE_TYPE.OrganisationRoles,
						to: { name: 'OrganizationRoles' },
						if: this.self.can('OrganizationRoleRead'),
					},
					{
						label: this.$t('common.navigation.tags'),
						favoritetype: FAVORITE_TYPE.OrganisationTags,
						to: { name: 'OrganizationTags' },
						if: this.self.can('TagRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.settings'),
				fontIcon: 'icon-interface-setting-cog',
				if: this.self.canOneOf(
					'CompanyDigitalSafeDocumentRead',
					'AgencyNewsRead',
					'Statistics',
					'ConditionRead',
					'CompanySettingUpdate',
				),
				children: [
					{
						label: this.$t('common.navigation.archives'),
						favoritetype: FAVORITE_TYPE.ParametresArchives,
						id: 'archives',
						to: { name: 'DigitalSafe' },
						if: this.self.can('CompanyDigitalSafeDocumentRead'),
					},
					{
						label: this.$t('common.news'),
						favoritetype: FAVORITE_TYPE.ParametresActualites,
						to: { name: 'AgencyNews' },
						if: this.self.can('AgencyNewsRead'),
					},
					{
						label: this.$t('common.navigation.statistics'),
						favoritetype: FAVORITE_TYPE.ParametresStatistiques,
						to: { name: 'Statistic' },
						if: this.self.can('Statistics'),
					},
					{
						label: this.$t('common.navigation.cgvCtt'),
						favoritetype: FAVORITE_TYPE.ParametresCgvCtt,
						to: { name: 'CgvCttBase' },
						if: this.self.can('ConditionRead'),
					},
					{
						label: this.$t('common.settings'),
						favoritetype: FAVORITE_TYPE.ParametresParametres,
						to: { name: 'SettingsReminders' },
						if: this.self.can('CompanySettingUpdate'),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.navigation.guides'),
				fontIcon: 'icon-computer-logo-safari',
				children: [
					{
						label: this.$t('common.navigation.agencyUserGuide'),
						callback: () => window.open(
							`https://enso-groupe.atlassian.net/wiki/external/NWQzZjQ1NjkyODUzNGZjNzk1YzY5MmFlYzMzMDRkMDU`,
							'_blank',
						),
					},
					{
						label: this.$t('common.navigation.clientUserGuide'),
						callback: () => window.open(
							`https://enso-groupe.atlassian.net/wiki/external/YTRmMDBiYzc2YWFlNDI0MTgzMWUwZWZiMmUyMGI4NTg`,
							'_blank',
						),
					},
					{
						label: this.$t('user.tempWorkerUserGuide'),
						callback: () => window.open(
							`https://enso-groupe.atlassian.net/wiki/external/Njc0Yjk1N2RmZTRjNDlhMTllMTQyOGRiZjJkMWRjNDU`,
							'_blank',
						),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.navigation.helpAndSupport'),
				fontIcon: 'icon-interface-help-question-circle',
				callback: () => window.open('https://support.enso-groupe.fr/', '_blank'),
			},
		];

		return nav;
	}

	private get clientNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: this.$t('common.navigation.documents'),
				fontIcon: 'icon-interface-file-folder',
				if: this.self.canOneOf(
					'ClientContractRead',
					'BillRead',
					'RegisterRead',
					'HandicappedWorkerListRead',
					'ClientProvisionDocumentRead',
				),
				children: [
					{
						label: this.$t('common.navigation.contracts'),
						favoritetype: FAVORITE_TYPE.MesDocumentsContrats,
						to: { name: 'ClientContract' },
						if: this.self.can('ClientContractRead'),
					},
					{
						label: this.$t('common.navigation.invoices'),
						favoritetype: FAVORITE_TYPE.MesDocumentsFactures,
						to: { name: 'Bill' },
						if: this.self.can('BillRead'),
					},
					{
						label: this.$t('common.personnelRegisters'),
						favoritetype: FAVORITE_TYPE.MesDocumentsRegistresDuPersonnel,
						to: { name: 'Register' },
						if: this.self.can('RegisterRead'),
					},
					{
						label: this.$t('common.navigation.handicappedWorkersList'),
						favoritetype: FAVORITE_TYPE.MesDocumentsListeTravailleursHandicapes,
						to: { name: 'HandicappedWorkerList' },
						if: this.self.can('HandicappedWorkerListRead'),
					},
					{
						label: this.$t('business.statistics.documents.provisionDocuments'),
						favoritetype: FAVORITE_TYPE.MesDocumentsDocumentsMisADisposition,
						to: { name: 'ClientProvisionDocument' },
						if: this.self.can('ClientProvisionDocumentRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.navigation.processings'),
				fontIcon: 'icon-interface-edit-user-experience-design',
				if: this.self.canOneOf('TimeTicketRead', 'ClientContractRead', 'OrderRead'),
				children: [
					{
						label: this.$t('common.timeTickets'),
						favoritetype: FAVORITE_TYPE.MesTraitementsRelevesHeures,
						to: {
							name: 'TimeTicketYear',
							params: { year: new Date().getFullYear().toString() },
						},
						if: this.self.can('TimeTicketRead'),
					},
					{
						label: this.$t('documents.contractExtensions'),
						favoritetype: FAVORITE_TYPE.MesTraitementsProlongationsContrats,
						to: { name: 'ContractProlongation' },
						if: this.self.can('ClientContractRead'),
					},
					{
						label: this.$t('common.navigation.orders'),
						favoritetype: FAVORITE_TYPE.MesTraitementsCommandes,
						to: { name: 'Order' },
						if: this.self.can('OrderRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.navigation.organization'),
				fontIcon: 'icon-interface-hierarchy-2',
				if: this.self.canOneOf(
					'OrganizationRead',
					'UserRead',
					'OrganizationRoleRead',
					'TagRead',
					'OrganizationUpdate',
					'MissionPlace',
				),
				children: [
					{
						label: this.$t('common.navigation.visionOverview'),
						favoritetype: FAVORITE_TYPE.OrganisationVisionDensemble,
						to: { name: 'OrganizationDashboard' },
						if: this.self.can('OrganizationRead'),
					},
					{
						label: this.$t('common.companies'),
						favoritetype: FAVORITE_TYPE.OrganisationSocietes,
						to: { name: 'OrganizationCompany' },
						if: this.self.can('OrganizationRead'),
					},
					{
						label: this.$t('common.navigation.users'),
						favoritetype: FAVORITE_TYPE.OrganisationMesUtilisateurs,
						to: { name: 'OrganizationUser' },
						if: this.self.can('UserRead'),
					},
					{
						label: this.$t('common.navigation.roles'),
						favoritetype: FAVORITE_TYPE.OrganisationRoles,
						to: { name: 'OrganizationRoles' },
						if: this.self.can('OrganizationRoleRead'),
					},
					{
						label: this.$t('common.navigation.tags'),
						favoritetype: FAVORITE_TYPE.OrganisationTags,
						to: { name: 'OrganizationTags' },
						if: this.self.can('TagRead'),
					},
					{
						label: this.$t('common.settings'),
						favoritetype: FAVORITE_TYPE.OrganisationParametres,
						to: { name: 'Settings' },
						if: this.self.can('OrganizationUpdate'),
					},
					{
						label: this.$t('common.missionPlaces'),
						favoritetype: FAVORITE_TYPE.OrganisationLieuxDeMission,
						to: { name: 'MissionPlace' },
						if: this.self.can('MissionPlace'),
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.news'),
				fontIcon: 'icon-interface-content-file',
				to: { name: 'News' },
			},
			{
				label: this.$t('common.navigation.onlineHelp'),
				fontIcon: 'icon-computer-logo-safari',
				callback: () => window.open(
					`https://enso-groupe.atlassian.net/wiki/external/YTRmMDBiYzc2YWFlNDI0MTgzMWUwZWZiMmUyMGI4NTg`,
					'_blank',
				),
			},
		];

		return nav;
	}

	private get tempWorkerNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: this.$t('common.navigation.myDocuments'),
				line: true,
			},
			{
				label: this.$t('common.navigation.personalLife'),
				fontIcon: 'icon-interface-user-circle',
				to: { name: 'DigitalSafe' },
			},
			{
				label: this.ui.onLayoutNavCollapsed && !this.responsive.onPhone ? this.$t('common.navigation.professionalLifeShort') : this.$t('common.navigation.professionalLife'),
				fontIcon: 'icon-interface-setting-tool-box',
				children: [
					{
						label: this.$t('common.navigation.contracts'),
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleContrats,
						to: { name: 'TempWorkerContract' },
					},
					{
						label: this.$t('common.paySlips'),
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleBulletinsSalaire,
						to: { name: 'SalarySlip' },
					},
					{
						label: this.$t('common.workCertificates'),
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleCertificatsTravail,
						to: { name: 'Certificate' },
					},
					{
						label: this.$t('common.taxCertificates'),
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleAttestionsImpots,
						to: { name: 'WithholdingTaxAttestation' },
					},
					{
						label: this.$t('business.contractProlongation.justificatory'),
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleJustificatifsDeplacements,
						to: { name: 'TripAttestation' },
					},
					{
						label: this.$t('common.navigation.employerAttestationsFranceTravail'),
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleAttestationsPoleEmploi,
						to: { name: 'PoleEmploiAttestation' },
					},
					{
						label: this.$t('business.statistics.documents.provisionDocuments'),
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleDocumentsMisADisposition,
						to: { name: 'TempWorkerProvisionDocument' },
					},
				] as ArmNavItem[],
			},
			{
				label: this.$t('common.navigation.myProcessings'),
				line: true,
			},
			{
				label: this.$t('common.availabilities'),
				fontIcon: 'icon-interface-calendar-mark',
				favoritetype: FAVORITE_TYPE.MesTraitementsDisponibilites,
				to: { name: 'UserProfileAvailabilities' },
			},
			{
				label: this.$t('common.navigation.advancePaymentRequests'),
				fontIcon: 'icon-money-cash-file-dollar',
				favoritetype: FAVORITE_TYPE.MesTraitementsDemandesAcompte,
				to: { name: 'AdvancedPaymentDemand' },
			},
			{
				label: this.$t('common.timeTickets'),
				fontIcon: 'icon-interface-time-book-2',
				favoritetype: FAVORITE_TYPE.MesTraitementsRelevesHeuresInterimaire,
				to: { name: 'TimeTicket' },
			},
			{
				label: this.$t('common.jobOffers'),
				fontIcon: 'icon-shopping-business-handshake',
				favoritetype: FAVORITE_TYPE.MesTraitementsOffresDeMission,
				to: { name: 'JobOffer' },
			},
			{
				label: this.$t('common.navigation.myAgencies'),
				fontIcon: 'icon-shopping-store-phone',
				favoritetype: FAVORITE_TYPE.MesTraitementsMesAgences,
				to: { name: 'UserProfileCompanies' },
			},
			{ line: true },
			{
				label: this.$t('common.news'),
				fontIcon: 'icon-interface-content-file',
				to: { name: 'News' },
			},
			{
				label: this.$t('common.navigation.onlineHelp'),
				fontIcon: 'icon-computer-logo-safari',
				callback: () => window.open(
					`https://enso-groupe.atlassian.net/wiki/external/Njc0Yjk1N2RmZTRjNDlhMTllMTQyOGRiZjJkMWRjNDU`,
					'_blank',
				),
			},
		];

		return nav;
	}

	private get flatSluggedMenuItems (): ArmNavItem[] {
		return this.userTypeNavigation.flatMap((x) => {
			if (x.children?.length) {
				return x.children.map(y => ({
					...y,
					slug: this.menuItemSlug({
						...y,
						parent: x,
					}),
				}));
			}

			return {
				...x,
				slug: this.menuItemSlug(x),
			};
		});
	}

	private get commonNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: this.$t('admin.dashboard'),
				fontIcon: 'icon-interface-dashboard-layout-3',
				to: { name: 'Dashboard' },
				root: true,
				wrapperClass: 'arm-nav-menu-common',
			},
		];

		return nav;
	}

	private get favoriteNavigation () {
		const fav = this.flatSluggedMenuItems.filter(x => this.itemIsFavorite(x));

		const nav: ArmNavItem[] = [];

		if (fav.length) {
			nav.push(...[
				{
					label: this.$t('common.navigation.favorites'),
					sectionTitle: true,
					wrapperClass: 'arm-nav-menu-favorite',
				},
				...fav.map(x => ({
					...x,
					wrapperClass: 'arm-nav-menu-favorite',
					processing: false,
				})),
			] as ArmNavItem[]);
		}

		return nav;
	}

	private get mainNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: this.$t('common.navigation.menu'),
				sectionTitle: true,
				wrapperClass: 'arm-nav-menu-base-section-title',
				if: () => !!this.favoriteNavigation.length && !this.self.isTempWorker(),
			},
			...this.userTypeNavigation.map(x => ({
				...x,
				wrapperClass: x.line ? 'arm-nav-menu-classic arm-nav-menu-classic--line' : 'arm-nav-menu-classic',
				processing: false,
				children: x.children?.map(y => ({
					...y,
					processing: false,
				})),
			})),
		];

		return nav;
	}

	get navigation (): Record<string, ArmNavItem[]> {
		if ((this.ui.onLayoutNavBottom || this.ui.onLayoutNavCollapsed) && !this.responsive.onPhone) {
			return {
				main: [
					...this.commonNavigation,
					...this.mainNavigation,
				],
			};
		}
		else {
			return {
				common: this.commonNavigation,
				favorites: this.favoriteNavigation,
				main: this.mainNavigation,
			};
		}
	}

	private readonly wrapperId = 'mobile-nav-children-clone-wrapper';

	@Reactive private readonly state = {
		favorites: [] as IUserFavoriteModel[],
		touchTimer: 0,
		isScolling: false,
		currentOpenedMenuIndex: undefined as Undef<number>,
	};

	private windowResizeHandler = debounce(async () => {
		this.addTouchListeners();
	}, 17);

	constructor (
		private props: SharedMainNavProps,
		private emits: SharedMainNavEmits,
	) {
		super();
		this.iniNavAsync();

		window.addEventListener('resize', this.debouncedWindowResizeHandler.bind(this));
	}

	protected onBeforeUnmount () {
		window?.removeEventListener('resize', this.debouncedWindowResizeHandler.bind(this));
	}

	private debouncedWindowResizeHandler () {
		this.windowResizeHandler();
	}

	private async iniNavAsync () {
		// Get favorites
		this.state.favorites = await useUserFavoriteService().listAsync();

		nextTick(() => {
			this.addTouchListeners();
		});
	}

	private menuItemSlug (item: ArmNavSlugedItem) {
		const itemSlugArray = [];
		if (item.parent?.label) itemSlugArray.push(item.parent.label);
		itemSlugArray.push(item.label);

		return itemSlugArray.join('/');
	}

	itemIsFavorite (item: ArmNavSlugedItem) {
		return item.favoritetype !== undefined && this.state.favorites.mapKey('type').includes(item.favoritetype);
	}

	async toggleMenuItemFavoriteAsync (item: ArmNavSlugedItem) {
		if (item.favoritetype !== undefined && this.itemIsFavorite(item)) {
			const fav = this.state.favorites.findByKey(item.favoritetype, 'type');
			if (fav) {
				item.processing = true;
				await useUserFavoriteService().deleteAsync(fav.id);
				item.processing = false;
				this.state.favorites.deleteWhere('id', fav.id);
			}
		}
		else if (item.favoritetype !== undefined) {
			item.processing = true;
			const data = await useUserFavoriteService().addAsync({ type: item.favoritetype });
			item.processing = false;
			this.state.favorites.push(data);
		}
	}

	private addTouchListeners () {
		const el = this._navMain.value?.first()?._el?.();

		if (!!el) {
			el.addEventListener('mouseover', throttle(e => this.setFloatingUiAsync(e.target as HTMLElement), 100));
			el.addEventListener('click', e => this.setFloatingUiAsync(e.target as HTMLElement));
		}
	}

	private appendMobileNavChildrenCloneWrapper (dataIndex: number, children: HTMLElement) {
		const childrenCloneWrapper = document.createElement('div');
		childrenCloneWrapper.id = this.wrapperId;
		childrenCloneWrapper.classList.add('arm-nav', 'orion-nav-main');
		childrenCloneWrapper.setAttribute('data-index', dataIndex.toString());

		childrenCloneWrapper.append(children);

		document.body.append(childrenCloneWrapper);

		setTimeout(() => {
			if (this._navMain.value?.first()) {
				onClickOutside(this._navMain.value.first()?._el?.(), () => this.removeMobileNavChildrenCloneWrapper());
			}
		}, 10);
	}

	private removeMobileNavChildrenCloneWrapper () {
		document.querySelectorAll('.arm-nav-menu-classic--active').forEach(el => el.classList.remove('arm-nav-menu-classic--active'));

		this.state.currentOpenedMenuIndex = undefined;

		if (!this.mobileNavChildrenCloneWrapper) return;

		const targetMenuItemIndex = this.mobileNavChildrenCloneWrapper.getAttribute('data-index');

		if (targetMenuItemIndex) {
			const submenu = this.mobileNavChildrenCloneWrapper.children.item(0);
			const targetMenuItem = this._navMain.value
				?.first()?._el?.()
				?.querySelector(`.orion-nav-main__wrapper > .orion-nav-main__children > [data-index="${targetMenuItemIndex}"]`);

			if (targetMenuItem && submenu) {
				submenu.removeAttribute('style');
				targetMenuItem.append(submenu);
			}
		}

		setTimeout(() => {
			this.mobileNavChildrenCloneWrapper?.remove();
		}, 50);
	}

	private async setFloatingUiAsync (target: HTMLElement) {
		if (this.ui.onLayoutNavFull || this.responsive.onPhone) return;

		if (target.classList.contains('arm-nav-menu__item')) {
			target = target.parentElement as HTMLElement;
		}

		let parent = target.parentElement;

		if (parent?.classList.contains('orion-nav-main__item')) {
			parent = parent.parentElement;
		}

		const dataIndex = +(parent?.getAttribute('data-index') ?? 0);

		if (!!dataIndex && this.state.currentOpenedMenuIndex === dataIndex) {
			// stop here if it's the same submenu
			return;
		}

		this.removeMobileNavChildrenCloneWrapper();

		const children = target.nextElementSibling as HTMLElement;

		if (!!children && !!dataIndex) {
			nextTick(async () => {
				if (target.classList.contains('orion-nav-main__item') && parent?.classList.contains('arm-nav-menu-classic')) {
					parent.classList.add('arm-nav-menu-classic--active');

					this.state.currentOpenedMenuIndex = dataIndex;

					Object.assign(children.style, { opacity: '0' });

					this.appendMobileNavChildrenCloneWrapper(dataIndex, children);

					const { x, y } = await computePosition(target, children, {
						strategy: 'fixed',
						placement: this.ui.onLayoutNavCollapsed ? 'right-start' : 'top',
						middleware: [
							offset({ alignmentAxis: this.ui.onLayoutNavCollapsed ? -5 : 0 }),
							flip(),
							shift({ padding: 16 }),
						],
					});

					Object.assign(children.style, {
						left: `${x}px`,
						top: `${y}px`,
						opacity: '1',
					});

				}
			});
		}
	}

}
